import {
  ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, EventEmitter,
  HostBinding, HostListener, Input, OnChanges, OnDestroy, OnInit, Output, ViewChild,
} from '@angular/core';

/** One icon on the collapsed rail. `key` matches a `data-cfp-section` attribute inside the panel. */
export interface FilterRailSection {
  key: string;
  label: string;
  icon: string;
}

/**
 * Collapsible filter sidebar for the search pages | 2026-10-17
 *
 * Applied as an attribute on a page's existing filter panel, so the panel
 * element stays the flex/grid item, keeps its own class and therefore every
 * scoped rule the page already has. Only the chrome is added: a "Filters"
 * switch, a 64px icon rail when switched off, and an off-canvas drawer on
 * narrow screens.
 *
 * Visual conventions copy the settings icon rail (styles.css,
 * `.main-container > .left-section`) and its pin script (index.html): 64px
 * rail, --ebr-* tokens, Material Icons, the same easing, a reduced-motion
 * guard and a localStorage choice. The rail opens on click, not hover: a
 * hover-expanding filter panel spills over the results, which is why the
 * filter panels were taken off that rule in the first place.
 *
 * The projected filters are hidden with CSS, never removed, so their state,
 * subscriptions and API calls are exactly what they were.
 */
@Component({
  selector: '[appCollapsibleFilterPanel]',
  exportAs: 'filterPanel',
  templateUrl: './collapsible-filter-panel.component.html',
  styleUrls: ['./collapsible-filter-panel.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CollapsibleFilterPanelComponent implements OnInit, OnChanges, OnDestroy {
  private static nextId = 0;

  /** Per-page key for the remembered on/off choice. */
  @Input() storageKey = 'default';
  @Input() sections: FilterRailSection[] = [];
  /** Section key -> has a value applied. Drives the badge on the rail and the drawer button count. */
  @Input() active: Record<string, boolean> | null = null;
  @Input() label = 'Filters';
  /**
   * Width (px, max-width) at or below which the page already stacks the panel
   * above the results. Below it the panel becomes an off-canvas drawer, since
   * a side rail has no width to give back there.
   */
  @Input() drawerBelow = 767.98;
  /** Hide the visible "Filters" text on the switch (the accessible name stays), for a page whose panel already has a Filters heading. */
  @Input() compactToggle = false;

  /** Fired when a rail icon is clicked, after the panel re-opens, so the page can open that section. */
  @Output() sectionSelect = new EventEmitter<string>();

  @ViewChild('drawerClose') private drawerCloseBtn?: ElementRef<HTMLButtonElement>;
  @ViewChild('drawerTrigger') private drawerTriggerBtn?: ElementRef<HTMLButtonElement>;

  readonly bodyId = `cfp-body-${CollapsibleFilterPanelComponent.nextId++}`;

  /** The user's choice: true = switched off (icon rail). */
  private collapsedPref = false;
  drawerMode = false;
  drawerOpen = false;

  private drawerMq?: MediaQueryList;
  private readonly onDrawerMq = (e: MediaQueryListEvent) => {
    this.drawerMode = e.matches;
    if (!e.matches) {
      this.drawerOpen = false;
      this.setScrollLock(false);
    }
    this.cdr.markForCheck();
  };
  private scrollTimer?: ReturnType<typeof setTimeout>;

  constructor(private el: ElementRef<HTMLElement>, private cdr: ChangeDetectorRef) {}

  @HostBinding('class.cfp') readonly hostClass = true;
  @HostBinding('class.cfp--collapsed') get collapsed(): boolean { return this.collapsedPref && !this.drawerMode; }
  @HostBinding('class.cfp--drawer') get isDrawer(): boolean { return this.drawerMode; }
  @HostBinding('class.cfp--drawer-open') get isDrawerOpen(): boolean { return this.drawerMode && this.drawerOpen; }

  get activeCount(): number {
    return this.sections.filter((s) => this.isActive(s.key)).length;
  }

  ngOnInit(): void {
    this.collapsedPref = this.readPref();
    this.watchDrawerQuery();
  }

  ngOnChanges(): void {
    if (this.drawerMq && this.drawerMq.media !== this.mediaQuery()) this.watchDrawerQuery();
  }

  ngOnDestroy(): void {
    this.drawerMq?.removeEventListener?.('change', this.onDrawerMq);
    clearTimeout(this.scrollTimer);
    this.setScrollLock(false);
  }

  isActive(key: string): boolean { return !!this.active?.[key]; }

  trackByKey(_: number, s: FilterRailSection): string { return s.key; }

  toggle(): void {
    this.collapsedPref = !this.collapsedPref;
    this.writePref(this.collapsedPref);
  }

  /** Rail icon: switch the panel back on, let the page open the section, then bring it into view. */
  openSection(key: string): void {
    if (this.collapsedPref) {
      this.collapsedPref = false;
      this.writePref(false);
    }
    this.sectionSelect.emit(key);
    clearTimeout(this.scrollTimer);
    // Wait for the width transition so the measured offsets are final.
    this.scrollTimer = setTimeout(() => this.scrollToSection(key), this.reducedMotion() ? 0 : 380);
  }

  openDrawer(): void {
    this.drawerOpen = true;
    this.setScrollLock(true);
    setTimeout(() => this.drawerCloseBtn?.nativeElement.focus());
  }

  closeDrawer(): void {
    if (!this.drawerOpen) return;
    this.drawerOpen = false;
    this.setScrollLock(false);
    setTimeout(() => this.drawerTriggerBtn?.nativeElement.focus());
  }

  @HostListener('document:keydown.escape', ['$event'])
  onEscape(event: Event): void {
    // Leave Escape to an open select, menu or tooltip (they live in the CDK overlay).
    const target = event.target as HTMLElement | null;
    if (event.defaultPrevented || target?.closest?.('.cdk-overlay-container')) return;
    if (this.drawerMode && this.drawerOpen) {
      this.closeDrawer();
      this.cdr.markForCheck();
    }
  }

  private scrollToSection(key: string): void {
    const host = this.el.nativeElement;
    const target = host.querySelector<HTMLElement>(`[data-cfp-section="${key}"]`);
    if (!target) return;
    // Scroll the panel itself (it is its own scroller on every page), never the
    // results or the page: scrollIntoView would move every scrolling ancestor.
    const head = host.querySelector<HTMLElement>('.cfp-head');
    const offset = target.getBoundingClientRect().top - host.getBoundingClientRect().top
      - (head?.offsetHeight ?? 0) - 8;
    if (host.scrollHeight > host.clientHeight) {
      host.scrollTo({ top: host.scrollTop + offset, behavior: this.reducedMotion() ? 'auto' : 'smooth' });
    }
    // The rail button that had focus is gone now, so hand focus to the section;
    // a plain heading (IMPA, supplier list) is made focusable for that.
    let focusable = target.querySelector<HTMLElement>('button, input, a[href], [tabindex]');
    if (!focusable) {
      target.tabIndex = -1;
      focusable = target;
    }
    focusable.focus({ preventScroll: true });
  }

  /** The layout scrolls #rightside-content-hold, not the window; freeze it while the drawer is open. */
  private setScrollLock(on: boolean): void {
    if (typeof document === 'undefined') return;
    document.getElementById('rightside-content-hold')?.classList.toggle('cfp-scroll-lock', on);
  }

  private watchDrawerQuery(): void {
    this.drawerMq?.removeEventListener?.('change', this.onDrawerMq);
    if (typeof window === 'undefined' || !window.matchMedia) return;
    this.drawerMq = window.matchMedia(this.mediaQuery());
    this.drawerMode = this.drawerMq.matches;
    this.drawerMq.addEventListener?.('change', this.onDrawerMq);
  }

  private mediaQuery(): string { return `(max-width: ${this.drawerBelow}px)`; }

  private reducedMotion(): boolean {
    try { return window.matchMedia('(prefers-reduced-motion: reduce)').matches; } catch { return false; }
  }

  private get prefKey(): string { return `mpsFilterPanelCollapsed.${this.storageKey}`; }

  // Storage can be missing or throw (private mode, blocked site data); the
  // panel then simply starts switched on.
  private readPref(): boolean {
    try { return localStorage.getItem(this.prefKey) === '1'; } catch { return false; }
  }

  private writePref(collapsed: boolean): void {
    try { localStorage.setItem(this.prefKey, collapsed ? '1' : '0'); } catch { /* not remembered */ }
  }
}
